/**
 * SideCart Directive
 * Matches TypeScript side-cart.directive.ts exactly
 * Handles side cart open/close animations and state management
 */
// @ts-nocheck
import { SideCartManager } from './side-cart-manager.js';
import CartItems from './cart-items.js';
import CartManager from './cart-manager.js';

// Check if GSAP is available (load from CDN if needed)
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap) {
      resolve(window.gsap);
      return;
    }

    // Load GSAP from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
    script.onload = () => resolve(window.gsap);
    script.onerror = () => {
      console.warn('GSAP failed to load, using fallback animations');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

class SideCart {
  static selector = '.sideCart';
  static OPENED_CLASS_NAME = 'slideout--opened';
  static OPENED_BODY_CLASS_NAME = 'has-slideout';

  constructor(hostElement) {
    this.host = hostElement;
    this.sideCartManager = SideCartManager.getInstance();
    this.cartManager = CartManager.getInstance();

    // Get elements - matches TypeScript line 22-31
    this.mask = document.querySelector('.sideCart__mask');
    this.headerCount = this.host.querySelector('.sideCart__headerCount');
    this.subtotal = this.host.querySelector('.sideCart__totalLabel');
    this.currentFocusElement = null;
    this.agreeInput = this.host.querySelector('#agree');
    this.checkoutBtn = this.host.querySelector('.sideCart__checkoutButton');
    this.productHolder = this.host.querySelector('.sideCart__items');

    // Current cart state (will be updated via events)
    this.currentStatus = this.cartManager.getState();
    this.cartItems = null;
    this.gsap = null;

    this.init();
  }

  async init() {
    // Load GSAP
    this.gsap = await loadGSAP();

    // Direct conversion from TypeScript line 36-50
    if (this.productHolder) {
      this.cartItems = new CartItems(this.currentStatus.items, this.productHolder);
    }

    // Subscribe FIRST so onStatusChange is ready before any open() call
    this.unsubscribeStatus = this.sideCartManager.subscribe(this.onStatusChange.bind(this));

    // Subscribe to cart manager state changes (if RxJS available, otherwise use events)
    if (this.cartManager.stateChange$) {
      this.cartManager.stateChange$.subscribe(this.onCartChange.bind(this));
    } else {
      // Fallback: listen to cart:update events
      window.addEventListener('cart:update', () => {
        this.cartManager.get().then(cart => this.onCartChange(cart));
      });
    }

    // Now safe to open — subscriber is already registered above
    if (window.location.hash && window.location.hash === '#open-sidecart') {
      this.sideCartManager.open();
      console.log("run")
    }
    window.addEventListener('cart:update', this.onCartEmitUpdate.bind(this));

    const cart = this.cartManager.getState();
    this.updateSubTotal(cart);
  }

  bindEvents() {
    // Matches TypeScript line 56-60
    if (this.mask) {
      this.mask.addEventListener('click', this.onMaskClick.bind(this));
    }

    if (this.productHolder) {
      this.productHolder.addEventListener('scroll', this.onItemsScroll.bind(this));
    }

    if (this.agreeInput) {
      this.agreeInput.addEventListener('change', this.onAgreeChange.bind(this));
    }

    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', this.submitForm.bind(this));
    }
  }

  unbindEvents() {
    // Matches TypeScript line 64-68
    if (this.mask) {
      this.mask.removeEventListener('click', this.onMaskClick);
    }

    if (this.productHolder) {
      this.productHolder.removeEventListener('scroll', this.onItemsScroll);
    }

    if (this.agreeInput) {
      this.agreeInput.removeEventListener('change', this.onAgreeChange);
    }
  }

  onMaskClick() {
    // Matches TypeScript line 71-73
    this.sideCartManager.close();
  }

  onStatusChange(status) {
    // Matches TypeScript line 76-89
    const { OPENED_CLASS_NAME } = SideCart;
    const hasOpenClass = this.host.classList.contains(OPENED_CLASS_NAME);

    if (status === 'opened' && !hasOpenClass) {
      this.host.classList.add(OPENED_CLASS_NAME);
      this.host.setAttribute('tabindex', '0');
      this.updateCartState();
      this.animateOpen().then(() => {
        this.bindEvents();
      });
    } else if (status === 'closed' && hasOpenClass) {
      this.host.setAttribute('tabindex', '-1');
      this.host.classList.remove(OPENED_CLASS_NAME);
      this.animateClose().then(() => {
        this.unbindEvents();
      });
    }
  }

  async updateCartState() {
    // Use CartManager.get() which handles /cart?view=variant
    try {
      const cart = await this.cartManager.get();
      this.onCartChange(cart);
    } catch (e) {
      console.error('Error fetching cart:', e);
    }
  }

  onCartChange(cart) {
    // Direct conversion from TypeScript line 93-106
    if (cart.item_count === 0) {
      this.disableElements();
      this.productHolder.classList.add('sideCart__items--empty');
    } else {
      this.enableElements();
      this.productHolder.classList.remove('sideCart__items--empty');
    }
    if (this.cartItems) {
      this.cartItems.update(cart.items);
    }

    this.updateHeaderCount(cart);
    this.updateSubTotal(cart);
    this.currentStatus = cart;
  }


  updateHeaderCount(cart) {
    // Matches TypeScript line 108-122 EXACTLY
    if (!this.headerCount || !cart?.items) return;

    let itemCount = 0;
    cart.items.forEach(item => {
      if (item?.properties?._rugBundlePrice) {
        itemCount += 1;
      } else if (item?.properties?._rugBundleId) {
        return; // Skip hidden items
      } else {
        itemCount += item.quantity;
      }
    });

    const nbInCart = itemCount || 0;
    this.headerCount.innerHTML = `(${nbInCart})`;
  }

  updateSubTotal(cart) {
    // Matches TypeScript line 124-126
    if (!this.subtotal || !cart) return;

    // Format money (simplified - use Shopify formatMoney if available)
    const formattedPrice = this.formatMoney(cart.total_price);
    this.subtotal.innerHTML = formattedPrice;
  }

  // formatMoney(cents) {
  //   // Simple money formatter - matches Shopify's formatMoney utility
  //   if (window.Shopify && window.Shopify.formatMoney) {
  //     return window.Shopify.formatMoney(cents);
  //   }

  //   // Fallback formatter
  //   const dollars = (cents / 100).toFixed(2);
  //   return `$${dollars}`;
  // }
  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  submitForm() {
    // Matches TypeScript line 129-131
    if (this.checkoutBtn && this.checkoutBtn.closest('form')) {
      this.checkoutBtn.closest('form').submit();
    }
  }

  async animateOpen() {
    // Matches TypeScript line 133-156
    return new Promise((resolve) => {
      const { mask } = this;
      this.currentFocusElement = document.querySelector('*:focus');
      document.body.classList.add(SideCart.OPENED_BODY_CLASS_NAME);

      this.host.setAttribute('aria-expanded', 'true');

      if (this.gsap) {
        // Use GSAP animations
        this.gsap.killTweensOf(mask);
        this.gsap.killTweensOf(this.host);

        this.gsap.to(mask, {
          duration: 0.625,
          autoAlpha: 1,
          display: 'block'
        });

        this.gsap.to(this.host, {
          duration: 0.625,
          x: 0,
          ease: 'power4.out',
          onComplete: resolve
        });
      } else {
        // Fallback CSS animations
        if (mask) {
          mask.style.opacity = '1';
          mask.style.display = 'block';
        }
        this.host.style.transform = 'translateX(0)';
        setTimeout(resolve, 625);
      }
    });
  }

  async animateClose() {
    // Matches TypeScript line 159-186
    return new Promise((resolve) => {
      const { mask } = this;

      this.host.setAttribute('aria-expanded', 'false');

      if (this.gsap) {
        // Use GSAP animations
        this.gsap.killTweensOf(mask);
        this.gsap.killTweensOf(this.host);

        document.body.classList.remove(SideCart.OPENED_BODY_CLASS_NAME);

        this.gsap.to(this.host, {
          duration: 0.5,
          x: '100%',
          ease: 'power4.out'
        });

        this.gsap.to(mask, {
          duration: 0.5,
          autoAlpha: 0,
          display: 'none',
          onComplete: () => {
            if (this.currentFocusElement) {
              this.currentFocusElement.focus();
            }
            resolve();
          }
        });
      } else {
        // Fallback CSS animations
        document.body.classList.remove(SideCart.OPENED_BODY_CLASS_NAME);
        this.host.style.transform = 'translateX(100%)';
        if (mask) {
          mask.style.opacity = '0';
          setTimeout(() => {
            mask.style.display = 'none';
            if (this.currentFocusElement) {
              this.currentFocusElement.focus();
            }
            resolve();
          }, 500);
        } else {
          resolve();
        }
      }
    });
  }

  disableElements() {
    // Matches TypeScript line 188-193
    const toDisable = document.querySelectorAll('[disable-when-empty]');
    toDisable.forEach(el => {
      el.setAttribute('disable-when-empty', 'disabled');
    });
  }

  enableElements() {
    // Matches TypeScript line 195-200
    const toEnable = document.querySelectorAll('[disable-when-empty]');
    toEnable.forEach(el => {
      el.setAttribute('disable-when-empty', '');
    });
  }

  onAgreeChange(e) {
    // Matches TypeScript line 203-209
    if (e.target.checked) {
      this.enableCheckout();
    } else {
      this.disableCheckout();
    }
  }

  disableCheckout() {
    // Matches TypeScript line 211-213
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = true;
    }
  }

  enableCheckout() {
    // Matches TypeScript line 215-217
    if (this.checkoutBtn) {
      this.checkoutBtn.disabled = false;
    }
  }

  onItemsScroll(e) {
    // Matches TypeScript line 220-226
    if (!this.productHolder) return;

    if (e.target.scrollTop > 0) {
      this.productHolder.classList.add('sideCart__items--scrolling');
    } else {
      this.productHolder.classList.remove('sideCart__items--scrolling');
    }
  }

  async onCartEmitUpdate() {
    // Direct conversion from TypeScript line 230-233
    await this.cartManager.get();
    this.sideCartManager.open();
  }

  destroy() {
    // Cleanup
    if (this.unsubscribeStatus) {
      this.unsubscribeStatus();
    }
    window.removeEventListener('cart:update', this.onCartEmitUpdate);
    this.unbindEvents();
  }
}

// Initialize SideCart on DOM ready
function initSideCart() {
  const sideCartElement = document.querySelector(SideCart.selector);
  if (sideCartElement) {
    window.sideCartInstance = new SideCart(sideCartElement);
  }
}
// Initialize on DOM ready and section load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSideCart);
} else {
  initSideCart();
}

// Re-initialize on section load (Shopify sections)
document.addEventListener('shopify:section:load', initSideCart);
// Export for module usage
export default SideCart;

